/** Игровой стол: крупный график слева; этап, банк, места, прогноз и действия справа. */
import type { GameActionView, GameView, RoomView } from '@mt/protocol';
import { client, useApp } from '../net/client';
import { ACTION_TITLES, clock, cr, pts, useNow } from '../lib/format';
import { num, t, tm } from '../i18n';
import { Chart } from './Chart';
import { Controls } from './Controls';
import { GameSeats, RoomSeats } from './Seats';
import { Settlement } from './Settlement';
import { StageTrack } from './StageTrack';
import { WaitingRoom } from './WaitingRoom';
import { variantOfCategory, variantTitle } from '../lib/variants';

export function Table({ room }: { room: RoomView }) {
  const game = useApp((s) => s.game);
  const chart = useApp((s) => s.chart);
  const profile = useApp((s) => s.profile);
  const info = useApp((s) => s.info);
  const now = useNow();
  const showChart = game && chart?.gameId === game.gameId ? chart : null;
  const variant = variantOfCategory(info, room.categoryId);

  return (
    <div className="table-page">
      <div className="table-head">
        <button className="btn ghost tiny" onClick={() => client.leave()} title={room.meSeated && game && !game.settlement && game.me.participant ? t('table.head.leaveHint') : ''}>
          {t('table.head.lobby')}
        </button>
        <span className="title">
          {variant && <b className="head-variant">{variantTitle(variant)} · </b>}
          {t('table.head.title', { ante: room.ante })} <span className="dim">· {room.roomId}</span>
        </span>
        {game ? (
          <span className="badges">
            {game.instrument && <span className="badge open">{t('table.head.instrument', { symbol: game.instrument.symbol })}</span>}
            <span className={`badge ${game.mode === 'replay' ? 'warn' : 'live'}`}>{game.mode === 'replay' ? t('table.head.replay') : t('table.head.live')}</span>
            {game.source === 'synthetic' && <span className="badge warn">{t('table.head.synthetic')}</span>}
          </span>
        ) : null}
      </div>

      {!game ? (
        <WaitingRoom room={room} now={now} />
      ) : (
        <div className="table-grid">
          <div className="main-col">
            <StageTrack game={game} now={now} nextStartAt={room.status === 'results' ? room.nextStartAt : null} />
            {game.settlement ? (
              <div className="result-split">
                <Chart chart={showChart} startAt={game.startAt} minuteMs={game.minuteMs} forecast={game.me.forecast} settled />
                <Settlement game={game} s={game.settlement} />
              </div>
            ) : (
              <Chart chart={showChart} startAt={game.startAt} minuteMs={game.minuteMs} forecast={game.me.forecast} settled={false} />
            )}
            {!game.settlement && <InfoStrip game={game} />}
          </div>

          <aside className="side-col">
            {game && !game.settlement && (
              <section className="panel pot">
                <div>
                  <span className="dim">{t('table.pot.pot')}</span>
                  <b className="mono">{cr(game.pot)}</b>
                </div>
                <div>
                  <span className="dim">P₀</span>
                  <b className="mono">{pts(game.p0)}</b>
                </div>
                <div>
                  <span className="dim">{t('table.pot.ante')}</span>
                  <b className="mono">{cr(game.ante)}</b>
                </div>
              </section>
            )}
            {/* решения — сразу под банком, чтобы кнопки действий всегда были на виду */}
            {game && !game.settlement && <Controls game={game} now={now} />}
            <ReadyPanel room={room} game={game} balance={profile?.balance ?? 0} now={now} />
            <section className="panel seats-panel">
              <div className="row-title">
                <span>{game && !game.settlement ? t('table.seats.participants') : t('table.seats.atTable')}</span>
                <span className="dim small">
                  {t('table.seats.count', { n: num(game && !game.settlement ? game.seats.length : room.seats.length, 0), max: num(room.maxPlayers, 0) })}
                </span>
              </div>
              {game && !game.settlement ? <GameSeats game={game} now={now} /> : <RoomSeats room={room} />}
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}

/** Текст записи ленты: ходы собираются на клиенте из полей (язык интерфейса), прочие записи — по коду сообщения сервера. */
function actionText(a: GameActionView): string {
  if (a.kind !== 'action' || !a.action) return tm(a.msg, a.text);
  // райз в ленте — «райз до» (сумма — итоговая ставка игрока в круге)
  const title = a.action === 'raise' ? ACTION_TITLES.raiseTo : ACTION_TITLES[a.action];
  return `${a.name}: ${title}${a.amount ? ' ' + cr(a.amount) : ''}${a.auto ? ' · ' + t('common.auto') : ''}`;
}

function InfoStrip({ game }: { game: GameView }) {
  const actions = [...game.actions].reverse();
  return (
    <div className="info-strip">
      <section className="panel feed" aria-label={t('table.feed.title')}>
        <div className="row-title">
          <span>{t('table.feed.title')}</span>
          <span className="dim small">{t('table.feed.hint')}</span>
        </div>
        <ul className="feed-list">
          {actions.length === 0 && <li className="dim">{t('table.feed.empty')}</li>}
          {actions.map((a) => (
            <li key={a.seq} className={`feed-item ${a.kind} ${a.action ?? ''} ${game.seats.find((s) => s.playerId === a.playerId)?.isMe ? 'me' : ''}`}>
              <span className="mono dim">{clock(a.at)}</span>
              <span className="feed-round">{a.round === 0 ? t('table.feed.ante') : t('table.feed.round', { n: num(a.round, 0) })}</span>
              <span className="feed-text">{actionText(a)}</span>
            </li>
          ))}
        </ul>
      </section>
      <section className="panel reveal" aria-label={t('table.reveal.title')}>
        <div className="row-title">
          <span>{t('table.reveal.title')}</span>
          <span className="dim small" title={game.commitment.hash}>
            {t('table.reveal.commitment', { hash: game.commitment.hash.slice(0, 12) })}
          </span>
        </div>
        <div className="composition">
          {game.composition ? (
            game.composition.map((c) => (
              <span key={c.symbol} className="ticker">
                <b>{c.symbol}</b> {c.name}
              </span>
            ))
          ) : (
            <span className="dim">{t('table.reveal.compositionHidden')}</span>
          )}
        </div>
        <ul className="log">
          {[...game.revealLog].reverse().map((e, i) => (
            <li key={i}>
              <span className="mono dim">{clock(e.at)}</span> {tm(e.msg, e.text)}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ReadyPanel({ room, game, balance, now }: { room: RoomView; game: GameView | null; balance: number; now: number }) {
  const inActive = game && !game.settlement && game.me.participant;
  if (inActive) return null;
  if (!room.meSeated) {
    return (
      <section className="panel">
        <p className="dim">{t('table.ready.spectator')}</p>
      </section>
    );
  }
  const enough = balance >= room.ante;
  return (
    <section className="panel ready-panel">
      {room.meReady ? (
        <>
          <p>
            <b className="ok">{t('table.ready.confirmed')}</b> {t('table.ready.anteAtStart', { amount: room.ante })}
          </p>
          <button className="btn ghost" onClick={() => client.ready(false)}>
            {t('table.ready.cancel')}
          </button>
        </>
      ) : (
        <>
          <button className="btn primary wide" disabled={!enough} onClick={() => client.ready(true)}>
            {t(room.status === 'playing' || room.status === 'results' ? 'table.ready.joinNext' : 'table.ready.join', { amount: room.ante })}
          </button>
          {!enough && <p className="warn-line">{t('table.ready.notEnough')}</p>}
        </>
      )}
    </section>
  );
}
